/**
 * Text-to-3D Generation Service
 *
 * Handles the workflow from approval to 3D asset generation:
 * 1. Generate a preview model through Meshy
 * 2. Refine it into the final textured model
 * 3. Ask Firebase Functions to store Meshy files in Firebase Storage
 * 4. Register tokenized render URLs in Firestore and link the asset to the topic
 */

import { MeshyApiService } from './meshyApiService';

export interface TextTo3dGenerationOptions {
  textTo3dAssetId: string;
  prompt: string;
  chapterId: string;
  topicId: string;
  userId: string;
  artStyle?: 'realistic' | 'sculpture';
  aiModel?: 'meshy-5' | 'meshy-6' | 'meshy-7.1' | 'latest';
  collectionName?: 'text_to_3d_assets' | 'avatar_to_3d_assets';
}

export interface GenerationProgress {
  stage: 'generating' | 'downloading' | 'uploading' | 'linking' | 'completed' | 'failed';
  progress: number;
  message: string;
  error?: string;
}

type MeshyTextureSet = {
  base_color?: string;
  metallic?: string;
  normal?: string;
  roughness?: string;
};

function isHumanoidPrompt(prompt: string): boolean {
  return /\b(person|people|human|boy|girl|man|woman|child|teacher|student|character|avatar|doctor|nurse|worker|robot|humanoid)\b/i.test(prompt);
}

export class TextTo3dGenerationService {
  private meshyApiService: MeshyApiService;

  constructor() {
    this.meshyApiService = new MeshyApiService();
  }

  async generateFromApprovedAsset(
    options: TextTo3dGenerationOptions,
    onProgress?: (progress: GenerationProgress) => void
  ): Promise<{ success: boolean; meshyAssetId?: string; error?: string }> {
    const {
      textTo3dAssetId,
      prompt,
      chapterId,
      topicId,
      userId,
      artStyle = 'realistic',
      aiModel = 'meshy-7.1',
      collectionName = 'text_to_3d_assets',
    } = options;

    try {
      onProgress?.({
        stage: 'generating',
        progress: 0,
        message: 'Initiating 3D model generation...',
      });

      const modelToUse = aiModel || 'meshy-6';
      const generationRequest = {
        prompt: prompt.trim(),
        ...(modelToUse === 'meshy-5' ? { art_style: artStyle } : {}),
        ai_model: modelToUse as 'meshy-5' | 'meshy-6' | 'meshy-7.1' | 'latest',
        model_type: 'standard' as const,
        topology: 'triangle' as const,
        target_formats: ['glb'] as const,
        target_polycount: 30000,
        should_remesh: modelToUse === 'meshy-5',
        auto_size: true,
        origin_at: 'bottom' as const,
        pose_mode: isHumanoidPrompt(prompt) ? 'a-pose' as const : '' as const,
        moderation: true,
        ...(modelToUse === 'meshy-7.1' || modelToUse === 'latest' ? { geometry_resolution: '2k' as const } : {}),
      };

      console.log('Starting Meshy preview generation for text-to-3D asset:', {
        textTo3dAssetId,
        prompt: `${prompt.substring(0, 50)}...`,
        artStyle,
        aiModel: modelToUse,
      });

      onProgress?.({
        stage: 'generating',
        progress: 5,
        message: 'Creating preview (mesh generation)...',
      });

      const previewResponse = await this.meshyApiService.generateAsset(generationRequest);
      const previewTaskId = previewResponse.result;

      if (!previewTaskId) {
        throw new Error('Failed to start preview generation: No task ID received');
      }

      onProgress?.({
        stage: 'generating',
        progress: 10,
        message: 'Generating mesh (preview stage)...',
      });

      const previewAsset = await this.meshyApiService.pollForCompletion(previewTaskId, 120, 3000);
      if (previewAsset.status !== 'completed') {
        throw new Error(`Preview generation failed: ${previewAsset.error?.message || 'Unknown error'}`);
      }

      console.log('Preview stage completed, starting refine stage.');

      onProgress?.({
        stage: 'generating',
        progress: 50,
        message: 'Adding textures (refine stage)...',
      });

      const refineResponse = await this.meshyApiService.createRefineTask({
        preview_task_id: previewTaskId,
        enable_pbr: true,
        hd_texture: true,
        texture_resolution: '4k' as const,
        remove_lighting: true,
        texture_prompt: prompt.trim().slice(0, 600),
        ai_model: modelToUse as 'meshy-5' | 'meshy-6' | 'meshy-7.1' | 'latest',
        target_formats: ['glb'],
        auto_size: true,
        origin_at: 'bottom',
        moderation: true,
      });

      const refineTaskId = refineResponse.result;
      if (!refineTaskId) {
        throw new Error('Failed to start refine generation: No task ID received');
      }

      onProgress?.({
        stage: 'generating',
        progress: 60,
        message: 'Applying textures (refine stage)...',
      });

      const meshyAsset = await this.meshyApiService.pollForCompletion(refineTaskId, 120, 3000);
      if (meshyAsset.status !== 'completed') {
        throw new Error(`Refine generation failed: ${meshyAsset.error?.message || 'Unknown error'}`);
      }

      onProgress?.({
        stage: 'uploading',
        progress: 80,
        message: 'Saving generated model to Firebase Storage...',
      });

      const persistedAsset = await this.meshyApiService.finalizeGeneratedAsset({
        sourceAssetId: textTo3dAssetId,
        sourceCollection: collectionName,
        chapterId,
        topicId,
        userId,
        prompt,
        name: prompt.substring(0, 100) || 'Generated 3D Asset',
        artStyle,
        aiModel,
        meshyId: refineTaskId,
        meshyPreviewId: previewTaskId,
        modelUrls: meshyAsset.metadata?.model_urls || {},
        thumbnailUrl: meshyAsset.thumbnailUrl,
        textureUrls: meshyAsset.metadata?.texture_urls as MeshyTextureSet[] | undefined,
        metadata: meshyAsset.metadata as Record<string, unknown> | undefined,
      });

      const meshyAssetId = String(persistedAsset.id || persistedAsset.asset_id || '');
      if (!meshyAssetId) {
        throw new Error('Generated asset was stored but no asset ID was returned');
      }

      console.log('Stored generated 3D asset with render URL:', {
        meshyAssetId,
        renderUrl: persistedAsset.render_url,
      });

      onProgress?.({
        stage: 'completed',
        progress: 100,
        message: 'Asset generated and ready!',
      });

      return {
        success: true,
        meshyAssetId,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error generating 3D asset from text-to-3D:', error);

      onProgress?.({
        stage: 'failed',
        progress: 0,
        message: 'Generation failed',
        error: errorMessage,
      });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

export const textTo3dGenerationService = new TextTo3dGenerationService();
